import Icon from '@/components/ui/AppIcon';

interface DifferentiatorCardProps {
  icon: string;
  title: string;
  description: string;
  delay?: string;
}

function DifferentiatorCard({ icon, title, description, delay }: DifferentiatorCardProps) {
  return (
    <div 
      className="glass-card rounded-2xl p-8 hover:shadow-xl transition-all duration-300 reveal group"
      style={{ animationDelay: delay }}
    >
      <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
        <Icon name={icon as any} size={32} className="text-primary" />
      </div>
      <h3 className="text-heading-4 text-foreground mb-3 font-bold">{title}</h3>
      <p className="text-base text-muted-foreground leading-relaxed">{description}</p>
    </div>
  );
}

export default function DifferentiatorsSection() {
  const differentiators = [
    {
      id: 'diff_quality',
      icon: 'StarIcon',
      title: 'Premium Quality Products',
      description: 'Dutch-formulated concentrates meeting international quality standards with rigorous testing and quality control.',
    },
    {
      id: 'diff_expertise',
      icon: 'AcademicCapIcon',
      title: 'Local Expertise',
      description: 'Deep understanding of Ugandan poultry farming conditions with on-ground support and technical guidance.',
    },
    {
      id: 'diff_support',
      icon: 'UserGroupIcon',
      title: 'Farmer Support',
      description: 'Dedicated technical assistance, training programs, and ongoing consultation to maximize your farm success.',
    },
    {
      id: 'diff_pricing',
      icon: 'CurrencyDollarIcon',
      title: 'Competitive Pricing',
      description: 'Best value for premium quality with flexible payment options and bulk discounts for distributors.',
    },
    {
      id: 'diff_delivery',
      icon: 'TruckIcon',
      title: 'Fast Delivery',
      description: 'Reliable supply chain ensuring timely delivery across Uganda with efficient logistics network.',
    },
    {
      id: 'diff_breeds',
      icon: 'SparklesIcon',
      title: 'Premium Breeds Focus',
      description: 'Specialized formulations optimized for high-performance layer and broiler breeds for maximum productivity.',
    },
  ];

  return (
    <section className="py-section px-6 bg-gradient-to-b from-green-50/30 to-white">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16 reveal">
          <p className="text-primary font-bold uppercase tracking-wider text-sm mb-4">What Sets Us Apart</p>
          <h2 className="text-heading-2 md:text-[3rem] lg:text-heading-1 leading-tight text-foreground mb-6">
            Your Trusted Partner in <br />
            <span className="gradient-text">Poultry Excellence</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Bloom Animal Feeds combines European expertise with local knowledge to deliver exceptional value to Ugandan poultry farmers.
          </p>
        </div>

        {/* Differentiators Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {differentiators.map((item, index) => (
            <DifferentiatorCard
              key={item.id}
              icon={item.icon}
              title={item.title}
              description={item.description}
              delay={`${index * 0.1}s`}
            />
          ))}
        </div>

        {/* CTA Button */}
        <div className="text-center mt-16 reveal" style={{ animationDelay: '0.6s' }}>
          <a
            href="/contact"
            className="group inline-flex items-center justify-center gap-3 bg-[#66BB6A] text-white px-12 py-6 rounded-full text-lg font-bold hover:bg-[#2E7D32] hover:scale-110 transition-all duration-300 shadow-2xl hover:shadow-[0_25px_60px_rgba(102,187,106,0.5)]">
            <Icon name="PhoneIcon" size={24} className="group-hover:rotate-12 transition-transform duration-300" />
            <span>Contact Us Today</span>
          </a>
        </div>
      </div>
    </section>
  );
}